import { getReportLanguage } from "../../utils/reportLanguage";
import { useMemo, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Grid, Paper, Stack, TextField, Typography, MenuItem, FormControlLabel, Switch } from "@mui/material";
import FeasibilityShell from "./FeasibilityShell";
import { useHistory } from "../../context/HistoryContext";
import { useFile } from "../../context/FileContext";
import { DEFAULT_CONFIG } from "../../components/config/defaultConfig";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AppDatePicker from "../../components/common/AppDatePicker";
import { useCurrentUserAccess } from "../../hooks/useCurrentUserAccess";
import { useIsGreek, useTranslateText } from "../../context/LanguageContext";
import { isBtmSettlement } from "../../components/config/btm";
import CommercialOfferFields from "./CommercialOfferFields";
import { COMMERCIAL_OFFER_SECTION_ID, commercialProjectOfferMode } from "../../components/config/commercialOffer";

const EUROPE_PHONE_CODES = [
  { code: "+30", country: "Greece", flag: "🇬🇷" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+34", country: "Spain", flag: "🇪🇸" },
  { code: "+39", country: "Italy", flag: "🇮🇹" },
  { code: "+31", country: "Netherlands", flag: "🇳🇱" },
  { code: "+32", country: "Belgium", flag: "🇧🇪" },
  { code: "+41", country: "Switzerland", flag: "🇨🇭" },
  { code: "+43", country: "Austria", flag: "🇦🇹" },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧" },
  { code: "+45", country: "Denmark", flag: "🇩🇰" },
  { code: "+46", country: "Sweden", flag: "🇸🇪" },
  { code: "+47", country: "Norway", flag: "🇳🇴" },
  { code: "+48", country: "Poland", flag: "🇵🇱" },
  { code: "+420", country: "Czech Republic", flag: "🇨🇿" },
  { code: "+421", country: "Slovakia", flag: "🇸🇰" },
  { code: "+36", country: "Hungary", flag: "🇭🇺" },
  { code: "+40", country: "Romania", flag: "🇷🇴" },
  { code: "+359", country: "Bulgaria", flag: "🇧🇬" },
  { code: "+385", country: "Croatia", flag: "🇭🇷" },
  { code: "+386", country: "Slovenia", flag: "🇸🇮" },
  { code: "+381", country: "Serbia", flag: "🇷🇸" },
  { code: "+382", country: "Montenegro", flag: "🇲🇪" },
  { code: "+387", country: "Bosnia & Herzegovina", flag: "🇧🇦" },
  { code: "+389", country: "North Macedonia", flag: "🇲🇰" },
  { code: "+355", country: "Albania", flag: "🇦🇱" },
  { code: "+370", country: "Lithuania", flag: "🇱🇹" },
  { code: "+371", country: "Latvia", flag: "🇱🇻" },
  { code: "+372", country: "Estonia", flag: "🇪🇪" },
  { code: "+353", country: "Ireland", flag: "🇮🇪" },
  { code: "+354", country: "Iceland", flag: "🇮🇸" },
  { code: "+351", country: "Portugal", flag: "🇵🇹" },
  { code: "+356", country: "Malta", flag: "🇲🇹" },
  { code: "+357", country: "Cyprus", flag: "🇨🇾" },
  { code: "+373", country: "Moldova", flag: "🇲🇩" },
  { code: "+380", country: "Ukraine", flag: "🇺🇦" },
];

// Permitting & consents status options. Empty ("") renders as "To be confirmed"
// in the report, so an untouched project honestly flags consents as pending DD.
const PERMIT_STATUS_OPTIONS = [
  { value: "", label: "To be confirmed" },
  { value: "Not started", label: "Not started" },
  { value: "In progress", label: "In progress" },
  { value: "Secured", label: "Secured" },
  { value: "Not applicable", label: "Not applicable" },
];

function todayDateString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function setPathInObject(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = { ...(cur[k] ?? {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

// A demo account always produces illustrative reports. The run endpoint
// forces clientInfo.demoMode for demo accounts (api/routers/feasibility.py),
// so the switch is shown on and locked here, with the reason.
export const DEMO_REPORT_LOCKED_TEXT =
  "Demo accounts always produce illustrative reports: the client is neutral, there is no " +
  "confidentiality stamp and every page carries the ILLUSTRATIVE watermark.";

export default function FeasibilityGeneral() {
  const history = useHistory();
  const { portfolio, currentProject } = useFile();
  const { isDemoUser } = useCurrentUserAccess();
  const isGreek = useIsGreek();
  const translateText = useTranslateText();
  const { hash } = useLocation();
  const cfg = useMemo(() => history.present ?? DEFAULT_CONFIG, [history.present]);
  const isBtm = isBtmSettlement(cfg.settlementType);
  const regulatoryOffer = commercialProjectOfferMode(cfg);
  const hasCommercialOffer = isBtm || regulatoryOffer;
  useEffect(() => {
    if (hasCommercialOffer && hash === `#${COMMERCIAL_OFFER_SECTION_ID}`) {
      const section = document.getElementById(COMMERCIAL_OFFER_SECTION_ID);
      section?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      section?.focus({ preventScroll: true });
    }
  }, [hash, hasCommercialOffer]);
  const mergedCfg = useMemo(() => {
    const base = DEFAULT_CONFIG;
    const next = { ...base, ...cfg };

    next.clientInfo = {
      ...base.clientInfo,
      ...(cfg.clientInfo || {}),
    };

    next.engInfo = {
      ...base.engInfo,
      ...(cfg.engInfo || {}),
    };

    next.metaInfo = {
      ...base.metaInfo,
      ...(cfg.metaInfo || {}),
      prep_date:
        cfg.metaInfo?.prep_date_auto === false
          ? (cfg.metaInfo?.prep_date ?? todayDateString())
          : todayDateString(),
    };

    next.permitting = {
      ...base.permitting,
      ...(cfg.permitting || {}),
    };

    return next;
  }, [cfg]);


  function SectionHeader({ icon, title, description }) {
    return (
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography variant="subtitle1" sx={sectionTitleSx}>
            {title}
          </Typography>
        </Stack>
        <Typography sx={sectionDescSx}>{description}</Typography>
      </Stack>
    );
  }

  const setField = useCallback((path, value) => {
    history.commit(setPathInObject(mergedCfg, path, value));
  }, [history, mergedCfg]);

  const setMetaPatch = useCallback((patch) => {
    history.commit(
      setPathInObject(mergedCfg, "metaInfo", { ...mergedCfg.metaInfo, ...patch })
    );
  }, [history, mergedCfg]);

  const inputLabelSx = {
    color: "primary.main",
    fontWeight: 600,
    letterSpacing: "0.02em",
    fontSize: "0.95rem",
    whiteSpace: "nowrap",
    lineHeight: 1.25,
    maxWidth: "none",
    "&.Mui-focused": { color: "primary.main" },
  };
  const fieldSx = {
    "& .MuiInputBase-root": { minHeight: 56 },
    "& .MuiInputBase-input": { py: 1.25 },
  };
  const sectionPaperSx = {
    p: 2.5,
    borderRadius: 3,
    border: "1px solid",
    borderColor: "divider",
    boxShadow: (theme) =>
      theme.palette.mode === "dark"
        ? "0 6px 20px rgba(0,0,0,0.18)"
        : "0 6px 18px rgba(0,0,0,0.06)",
    background: (theme) =>
      theme.palette.mode === "dark"
        ? "linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.01))"
        : "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(250,250,250,0.98))",
  };

  const sectionTitleSx = {
    fontWeight: 700,
    lineHeight: 1.2,
  };

  const sectionDescSx = {
    color: "text.secondary",
    fontSize: "0.92rem",
  };

  const fieldSxEnhanced = {
    ...fieldSx,
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.08)",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.12)",
      },
    },
    "& .MuiFormHelperText-root": {
      mx: 0.25,
      mt: 0.75,
      lineHeight: 1.35,
    },
  };

  const commonInputProps = {
    InputLabelProps: { sx: inputLabelSx, shrink: true },
  };

  const client = mergedCfg.clientInfo;
  const eng = mergedCfg.engInfo;
  const meta = mergedCfg.metaInfo;
  const errors = {
    clientName: !client.clName?.trim(),
    engName: !eng.eng_name?.trim(),
    engTitle: !eng.eng_title?.trim(),
    engCompany: !eng.eng_company?.trim(),
    prepDate: !meta.prep_date?.trim(),
    apprBy: !meta.study_approvedBy?.trim(),
    studyVers: !meta.study_version?.trim(),
    clientEmail:
      (client.client_email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.client_email)) || !client.client_email?.trim(),
    engEmail:
      (eng.eng_mail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(eng.eng_mail)) || !eng.eng_mail?.trim(),
  };

  return (
    <FeasibilityShell
      title="General"
      subtitle="Client, Engineering, and Study Metadata."
    >

      <Stack spacing={2}>
        <Paper elevation={2} sx={sectionPaperSx}>
          <Stack spacing={2.25}>
            <SectionHeader
              icon={<BusinessOutlinedIcon color="primary" fontSize="small" />}
              title="Client Info"
              description="Basic client and contact details for the feasibility study record."
            />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Project Title (reports & teaser)"
                  value={client.project_title || ""}
                  onChange={(e) =>
                    setField("clientInfo.project_title", e.target.value)
                  }
                  placeholder={
                    mergedCfg.projectName?.trim()
                      ? `Default: ${mergedCfg.projectName.trim()}`
                      : "e.g. Firm Block PPA Bankability Screening"
                  }
                  helperText="Headline used on the proposal PDF and the public teaser. Leave empty to use the Project Description from the General page."
                  fullWidth
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label={isGreek ? "Γλώσσα αναφορών" : "Report Language"}
                  value={getReportLanguage(cfg)}
                  onChange={(e) => setField("reportLanguage", e.target.value)}
                  helperText={isGreek
                    ? "Γλώσσα των νέων αναφορών: σχεδίαση ΦΒ, παραγωγή ΦΒ, μονογραμμικό, διαστασιολόγηση, πρόταση και teasers. Ανεξάρτητη από τη γλώσσα της εφαρμογής. Τα υπάρχοντα αρχεία κρατούν τη γλώσσα τους· ξανατρέξτε τη μελέτη για νέα πρόταση και teasers."
                    : "Language for newly generated reports, including PV design, PV generation, the single-line diagram, the sizing, the proposal and teasers. Independent of the app language. Existing files keep their original language; run the study again to regenerate its proposal and teasers."}
                  fullWidth
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="el">Ελληνικά</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  label="Client Name"
                  value={client.clName || ""}
                  autoComplete="organization"
                  onChange={(e) => setField("clientInfo.clName", e.target.value)}
                  placeholder="e.g. ABC Solar Development Ltd."
                  helperText="Company or individual commissioning the study."
                  error={errors.clientName}
                  fullWidth
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Intended recipient (pre-export check)"
                  value={client.intendedRecipient || ""}
                  onChange={(e) => setField("clientInfo.intendedRecipient", e.target.value)}
                  placeholder="Must match the client name"
                  helperText="Optional QA gate: if stated and it does not match the Client Name, the export is BLOCKED instead of shipping a mismatched confidential report."
                  fullWidth
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isDemoUser || Boolean(client.demoMode)}
                      disabled={isDemoUser}
                      onChange={(e) => setField("clientInfo.demoMode", e.target.checked)}
                    />
                  }
                  label="Demo / illustrative report (neutral client, no confidentiality stamp, ILLUSTRATIVE watermark)"
                />
                {isDemoUser ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {DEMO_REPORT_LOCKED_TEXT}
                  </Typography>
                ) : null}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Person"
                  value={client.contact_person || ""}
                  autoComplete="name"
                  onChange={(e) =>
                    setField("clientInfo.contact_person", e.target.value)
                  }
                  placeholder="e.g. John Smith"
                  helperText="Primary point of contact for technical or commercial communication."
                  fullWidth
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Contact Role"
                  value={client.contact_role || ""}
                  autoComplete="organization-title"
                  onChange={(e) =>
                    setField("clientInfo.contact_role", e.target.value)
                  }
                  placeholder="e.g. Project Manager"
                  helperText="Role of the contact person within the client organization."
                  fullWidth
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  required
                  label="Client Email"
                  type="email"
                  autoComplete="email"
                  value={client.client_email || ""}
                  onChange={(e) =>
                    setField("clientInfo.client_email", e.target.value)
                  }
                  placeholder="name@company.com"
                  helperText="Main email for study correspondence."
                  fullWidth
                  error={errors.clientEmail}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Grid container spacing={1}>

                  {/* Phone Code */}
                  <Grid item xs={4}>
                    <TextField
                      select
                      label="Code"
                      value={client.client_phone_code || "+30"}
                      onChange={(e) =>
                        setField("clientInfo.client_phone_code", e.target.value)
                      }
                      fullWidth
                      sx={fieldSxEnhanced}
                      {...commonInputProps}
                    >
                      {EUROPE_PHONE_CODES.map((c) => (
                        <MenuItem key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Phone Number */}
                  <Grid item xs={8}>
                    <TextField
                      label="Phone"
                      type="tel"
                      value={client.client_phone || ""}
                      autoComplete="tel"
                      onChange={(e) =>
                        setField("clientInfo.client_phone", e.target.value)
                      }
                      placeholder="69X XXX XXXX"
                      helperText="Phone number for urgent coordination if needed."
                      fullWidth
                      sx={fieldSxEnhanced}
                      {...commonInputProps}
                    />
                  </Grid>

                </Grid>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Client Address"
                  value={client.client_address || ""}
                  autoComplete="street-address"
                  onChange={(e) =>
                    setField("clientInfo.client_address", e.target.value)
                  }
                  placeholder="Street, city, postal code, country"
                  helperText="Registered office or project-related address."
                  fullWidth
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Study Objective"
                  value={client.study_objective || ""}
                  onChange={(e) =>
                    setField("clientInfo.study_objective", e.target.value)
                  }
                  placeholder="Assess the technical and economic feasibility of a PV + BESS project, including energy yield, battery sizing, CAPEX/OPEX assumptions, grid limitations, and key financial indicators."
                  helperText="Example: technical feasibility, bankability, battery sizing, yield assessment, grid export constraints, or economic viability."
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Potential Offtakers"
                  value={(mergedCfg.potentialOfftakers || []).join("\n")}
                  onChange={(e) =>
                    setField("potentialOfftakers", e.target.value.split("\n"))
                  }
                  placeholder={"PPC\nMytilineos Energy & Metals\nOptimus Energy"}
                  helperText="Optional. One counterparty per line. Appears in the report's Potential Offtakers section."
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={5}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      label="Land Control / Site Rights"
                      value={mergedCfg.permitting?.landControl || ""}
                      onChange={(e) => setField("permitting.landControl", e.target.value)}
                      SelectProps={{ displayEmpty: true }}
                      helperText="Land ownership / lease / option status."
                      fullWidth
                      sx={fieldSxEnhanced}
                      {...commonInputProps}
                    >
                      {PERMIT_STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o.label} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      label="Grid Connection"
                      value={mergedCfg.permitting?.gridConnection || ""}
                      onChange={(e) => setField("permitting.gridConnection", e.target.value)}
                      SelectProps={{ displayEmpty: true }}
                      helperText="Connection offer / agreement status."
                      fullWidth
                      sx={fieldSxEnhanced}
                      {...commonInputProps}
                    >
                      {PERMIT_STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o.label} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      label="Environmental Permit"
                      value={mergedCfg.permitting?.environmental || ""}
                      onChange={(e) => setField("permitting.environmental", e.target.value)}
                      SelectProps={{ displayEmpty: true }}
                      helperText="Environmental / EIA approval status."
                      fullWidth
                      sx={fieldSxEnhanced}
                      {...commonInputProps}
                    >
                      {PERMIT_STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o.label} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      label="Construction Permit"
                      value={mergedCfg.permitting?.construction || ""}
                      onChange={(e) => setField("permitting.construction", e.target.value)}
                      SelectProps={{ displayEmpty: true }}
                      helperText="Building / construction permit status."
                      fullWidth
                      sx={fieldSxEnhanced}
                      {...commonInputProps}
                    >
                      {PERMIT_STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o.label} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Permitting & Consents Notes"
                  value={mergedCfg.permitting?.note || ""}
                  onChange={(e) => setField("permitting.note", e.target.value)}
                  placeholder="e.g. Grid connection offer expected Q3; EIA screening submitted; land option signed with purchase on financial close."
                  helperText="Optional. Appears in the report's Permitting & Consents section. Any status left blank shows as 'To be confirmed'."
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>


            </Grid>
          </Stack>
        </Paper>

        <Paper elevation={2} sx={sectionPaperSx}>
          <Stack spacing={2.25}>
            <SectionHeader
              icon={<EngineeringOutlinedIcon color="primary" fontSize="small" />}
              title="Engineering Info"
              description="Details of the engineer or organization preparing the study."
            />


            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  label="Engineer Name"
                  value={eng.eng_name || ""}
                  autoComplete="name"
                  onChange={(e) => setField("engInfo.eng_name", e.target.value)}
                  placeholder="e.g. Engineer Name"
                  helperText="Lead engineer or study author."
                  fullWidth
                  error={errors.engName}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  required
                  label="Title"
                  value={eng.eng_title || ""}
                  autoComplete="organization-title"
                  onChange={(e) => setField("engInfo.eng_title", e.target.value)}
                  placeholder="e.g. Energy Engineer"
                  helperText="Professional title of the person preparing the study."
                  fullWidth
                  error={errors.engTitle}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Registration"
                  value={eng.eng_registration || ""}
                  onChange={(e) =>
                    setField("engInfo.eng_registration", e.target.value)
                  }
                  placeholder="License / registration number"
                  helperText="Professional registration or certification identifier, if applicable."
                  fullWidth
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  required
                  label="Company"
                  value={eng.eng_company || ""}
                  autoComplete="organization"
                  onChange={(e) => setField("engInfo.eng_company", e.target.value)}
                  placeholder="e.g. MetEn Solutions"
                  helperText="Company or consulting entity issuing the study."
                  fullWidth
                  error={errors.engCompany}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  required
                  label="Email"
                  type="email"
                  value={eng.eng_mail || ""}
                  autoComplete="email"
                  onChange={(e) => setField("engInfo.eng_mail", e.target.value)}
                  placeholder="engineer@company.com"
                  helperText="Contact email of the study author or responsible engineer."
                  fullWidth
                  error={errors.engEmail}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>



              <Grid item xs={12} md={4}>
                <Grid container spacing={1}>

                  {/* Phone Code — engineer's own field (was mistakenly bound
                      to clientInfo.client_phone_code, which made the two
                      selects mirror each other) */}
                  <Grid item xs={4}>
                    <TextField
                      select
                      label="Code"
                      value={eng.eng_phone_code || "+30"}
                      onChange={(e) =>
                        setField("engInfo.eng_phone_code", e.target.value)
                      }
                      fullWidth
                      sx={fieldSxEnhanced}
                      {...commonInputProps}
                    >
                      {EUROPE_PHONE_CODES.map((c) => (
                        <MenuItem key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Phone Number */}
                  <Grid item xs={8}>
                    <TextField
                      label="Phone"
                      type="tel"
                      value={eng.eng_phone || ""}
                      autoComplete="tel"
                      onChange={(e) => setField("engInfo.eng_phone", e.target.value)}
                      placeholder="69X XXX XXXX"
                      helperText="Optional direct phone number."
                      fullWidth
                      sx={fieldSxEnhanced}
                      {...commonInputProps}
                    />
                  </Grid>

                </Grid>
              </Grid>

            </Grid>
          </Stack>
        </Paper>

        <Paper elevation={2} sx={sectionPaperSx}>
          <Stack spacing={2.25}>
            <SectionHeader
              icon={<FactCheckOutlinedIcon color="primary" fontSize="small" />}
              title="Study Metadata"
              description="Document control information for tracking revisions and approvals."
            />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <AppDatePicker
                  required
                  label="Preparation Date"
                  value={meta.prep_date || ""}
                  onChange={(val) => {
                    setMetaPatch({
                      prep_date: val,
                      prep_date_auto: val === todayDateString(),
                    });
                  }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  required
                  label="Study Version"
                  value={meta.study_version || ""}
                  onChange={(e) =>
                    setField("metaInfo.study_version", e.target.value)
                  }
                  placeholder="e.g. Rev 0"
                  helperText="Document revision label."
                  fullWidth
                  error={errors.studyVers}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  required
                  label="Approved By"
                  value={meta.study_approvedBy || ""}
                  onChange={(e) =>
                    setField("metaInfo.study_approvedBy", e.target.value)
                  }
                  placeholder="Reviewer / approver name"
                  helperText="Person or authority who approved the study."
                  fullWidth
                  error={errors.apprBy}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Approval Notes"
                  value={meta.study_approvalNotes || ""}
                  onChange={(e) =>
                    setField("metaInfo.study_approvalNotes", e.target.value)
                  }
                  placeholder={translateText("Internal review comments, approval conditions, or release notes")}
                  helperText="Optional notes regarding review status, approval assumptions, or revision remarks."
                  fullWidth
                  multiline
                  minRows={2}
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Document ID"
                  value={meta.document_id || ""}
                  onChange={(e) => setField("metaInfo.document_id", e.target.value)}
                  placeholder="e.g. FS-2026-001"
                  helperText="Internal reference or report identifier."
                  fullWidth
                  sx={fieldSxEnhanced}
                  {...commonInputProps}
                />
              </Grid>

            </Grid>
          </Stack>
        </Paper>

        {hasCommercialOffer ? (
          <CommercialOfferFields key={`${currentProject?.id || "new"}:${portfolio?.activeAssetId || "active"}`} value={cfg.commercialOffer} isGreek={isGreek}
            systemType={cfg.systemType} reportLanguage={getReportLanguage(cfg)}
            regulatoryMode={regulatoryOffer}
            logoSrc={portfolio?.project?.reportBranding?.logoDataUrl || cfg.reportBranding?.logoDataUrl}
            projectId={currentProject?.id || null}
            demoMode={isDemoUser || Boolean(client.demoMode)} sx={sectionPaperSx}
            onChange={(terms) => setField("commercialOffer", terms)} />
        ) : null}
      </Stack>
    </FeasibilityShell>
  );
}
